import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Draw as SignatureIcon,
  Assessment as StatusIcon,
  Description as PolicyIcon,
  Build as ToolsIcon,
  SmartToy as AIIcon,
  AutoStories as AutoStoriesIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  Equalizer as EqualizerIcon,
  Analytics as AnalyticsIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Build as BuildIcon,
  Description as DocumentIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

const DRAWER_WIDTH = 260;

const navigationItems = [
  {
    id: 'broker',
    section: 'Broker',
    icon: <PersonIcon />,
    items: [
      { title: 'Upload', path: '/upload-type' },
      { title: 'E-Signature', path: '/signature' },
      { title: 'Submitted', path: '/analysis' },
      { 
        title: 'Performance', 
        path: '/broker/performance',
        icon: <EqualizerIcon sx={{ color: '#1976d2' }} />
      }
    ]
  },
  {
    id: 'underwriter',
    section: 'Underwriter',
    icon: <DescriptionIcon />,
    items: [
      { 
        title: 'Review Queue',
        path: '/underwriter/policy-review' 
      },
      { 
        title: 'Review List',
        path: '/underwriter/approved' 
      }
    ]
  },
  {
    id: 'branchManager',
    section: 'Branch Manager',
    icon: <BusinessIcon />,
    items: [
      { 
        title: 'Upload', 
        path: '/upload-type',
        icon: <UploadIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'E-Signature', 
        path: '/signature',
        icon: <SignatureIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Submitted', 
        path: '/analysis',
        icon: <AssessmentIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Review', 
        path: '/underwriter/policy-review',
        icon: <DescriptionIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Performance', 
        path: '/branch-manager/performance',
        icon: <EqualizerIcon sx={{ color: '#1976d2' }} />
      }
    ]
  },
  {
    id: 'director',
    section: 'Director',
    icon: <AssessmentIcon />,
    items: [
      { 
        title: 'Upload', 
        path: '/upload-type',
        icon: <UploadIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'E-Signature', 
        path: '/signature',
        icon: <SignatureIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Submitted', 
        path: '/analysis',
        icon: <AssessmentIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Review', 
        path: '/underwriter/policy-review',
        icon: <DescriptionIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Underwriter', 
        path: '/underwriter-statistics',
        icon: <AssessmentIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'Performance', 
        path: '/broker-quality',
        icon: <EqualizerIcon sx={{ color: '#1976d2' }} />
      }
    ]
  },
  {
    id: 'tools',
    section: 'Tools',
    icon: <BuildIcon />,
    items: [
      { 
        title: 'Document Generation', 
        path: '/document-select',
        icon: <DocumentIcon sx={{ color: '#1976d2' }} />
      },
      { 
        title: 'AI Assistant', 
        path: '/ai-chat',
        icon: <AIIcon sx={{ color: '#1976d2' }} />
      }
    ]
  },
  {
    id: 'settings',
    section: 'Settings',
    icon: <SettingsIcon />,
    items: [
      { title: 'System Settings', path: '/settings/general' },
      { title: 'User Management', path: '/settings/users' }
    ]
  }
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({
    broker: true,
    underwriter: false,
    branchManager: false,
    director: false,
    tools: false,
    settings: false
  });

  const toggleSection = (sectionId) => {
    setExpanded(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e0e0e0'
        },
      }}
    >
      <Box 
        sx={{ 
          p: 3,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f5f5f5'
          }
        }}
        onClick={handleLogoClick}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <AutoStoriesIcon sx={{ fontSize: 28 }} />
          UW Compass
        </Typography>
      </Box>
      
      <Divider />
      
      <List>
        {navigationItems.map((section) => (
          <div key={section.id}>
            <ListItemButton 
              onClick={() => toggleSection(section.id)}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: '#1976d2' }}>
                {section.icon}
              </ListItemIcon>
              <ListItemText 
                primary={section.section}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              />
              {expanded[section.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={expanded[section.id]} timeout="auto" unmountOnExit>
              <List sx={{ pt: 0 }}>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => navigate(item.path)}
                    sx={{
                      pl: 6,
                      py: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.875rem'
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>

            <Divider sx={{ my: 2 }} />
          </div>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar; 